export interface LiveTutorialStep {
  readonly title: string;
  readonly prompt: string;
  readonly detail?: string;
  readonly iconText?: string;
  readonly iconKind?: 'text' | 'd20';
  readonly target?: () => HTMLElement | null;
  readonly placement?: 'bottom' | 'top' | 'left' | 'right';
  readonly placementOffsetY?: number;
  readonly manualAdvance?: boolean;
  readonly advanceText?: string;
  readonly waitingText?: string;
}

export interface LiveTutorialWidgetOptions {
  readonly mount: HTMLElement;
  readonly steps: readonly LiveTutorialStep[];
  readonly testId?: string;
  readonly zIndex?: number;
  readonly initialStepIndex?: number;
  readonly showHideButton?: boolean;
  readonly showProgress?: boolean;
  readonly minimizeOnHide?: boolean;
  readonly initialMinimized?: boolean;
  readonly minimizedText?: string;
  readonly enableKeyboardAdvance?: boolean;
  readonly mobileLayout?: 'target' | 'center';
  readonly getRightAvoidance?: () => number;
  readonly getProgressRightAvoidance?: () => number;
  readonly onComplete: () => void;
  readonly onHideTutorials?: () => void;
  readonly onMinimize?: () => void;
  readonly onExpand?: () => void;
  readonly onStepChange?: (index: number, step: LiveTutorialStep) => void;
  readonly canAdvance?: (index: number, step: LiveTutorialStep) => boolean;
}

export interface LiveTutorialWidgetHandle {
  readonly element: HTMLElement;
  readonly active: boolean;
  readonly minimized: boolean;
  advance(): void;
  layout(): void;
  pulse(): void;
  minimize(): void;
  expand(): void;
  dispose(): void;
}

const FONT_HEADLINE =
  "var(--typography-family-headline, 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif)";
const FONT_BODY =
  "var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)";

function isAdvanceKey(event: KeyboardEvent): boolean {
  return event.key === 'Enter' || event.key === ' ' || event.code === 'Space';
}

function createD20Icon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('data-testid', 'live-tutorial-d20-icon');
  svg.setAttribute('viewBox', '0 0 96 96');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.cssText = ['width: 100%', 'height: 100%', 'display: block'].join('; ');

  const back = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
  back.setAttribute('points', '48 5 87 29 79 74 48 91 17 74 9 29');
  back.setAttribute('fill', '#5B2CC6');
  back.setAttribute('stroke', '#24114E');
  back.setAttribute('stroke-width', '6');
  back.setAttribute('stroke-linejoin', 'round');
  svg.appendChild(back);

  for (const [points, fill] of [
    ['48 5 48 44 9 29', '#8D5CFF'],
    ['48 5 87 29 48 44', '#6D3EE2'],
    ['9 29 48 44 17 74', '#4B22A8'],
    ['87 29 48 44 79 74', '#3B188A'],
    ['17 74 48 44 48 91', '#6B38DF'],
    ['79 74 48 44 48 91', '#5424B8'],
  ] as const) {
    const facet = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    facet.setAttribute('points', points);
    facet.setAttribute('fill', fill);
    facet.setAttribute('stroke', 'rgba(255,255,255,0.22)');
    facet.setAttribute('stroke-width', '1.5');
    facet.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(facet);
  }

  const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
  text.textContent = '20';
  text.setAttribute('x', '48');
  text.setAttribute('y', '58');
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('font-size', '26');
  text.setAttribute('font-weight', '900');
  text.setAttribute('font-family', 'Arial Black, sans-serif');
  text.setAttribute('fill', '#FFFFFF');
  text.setAttribute('stroke', '#000000');
  text.setAttribute('stroke-width', '3');
  text.setAttribute('paint-order', 'stroke');
  svg.appendChild(text);

  return svg;
}

export function createLiveTutorialWidget(
  options: LiveTutorialWidgetOptions,
): LiveTutorialWidgetHandle {
  if (options.steps.length === 0) {
    throw new Error('createLiveTutorialWidget requires at least one step');
  }

  let disposed = false;
  let minimized = options.initialMinimized === true;
  let index = Math.min(
    Math.max(0, Math.floor(options.initialStepIndex ?? 0)),
    options.steps.length - 1,
  );

  const root = document.createElement('div');
  root.dataset.testid = options.testId ?? 'live-tutorial-widget';
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${options.zIndex ?? 640}`,
    'pointer-events: none',
    'box-sizing: border-box',
    'padding: max(18px, env(safe-area-inset-top)) max(18px, env(safe-area-inset-right)) max(18px, env(safe-area-inset-bottom)) max(18px, env(safe-area-inset-left))',
    'color: #FFFFFF',
  ].join('; ');

  const targetGlow = document.createElement('div');
  targetGlow.dataset.testid = 'live-tutorial-target-glow';
  targetGlow.style.cssText = [
    'position: fixed',
    'display: none',
    'border-radius: 10px',
    'border: 5px solid #FF3F6E',
    'box-shadow: 0 0 0 5px rgba(255,63,110,0.20), 0 0 28px rgba(255,63,110,0.85)',
    'pointer-events: none',
    'box-sizing: border-box',
    'transition: left 120ms ease, top 120ms ease, width 120ms ease, height 120ms ease',
  ].join('; ');
  root.appendChild(targetGlow);

  const progressShell = document.createElement('div');
  progressShell.dataset.testid = 'live-tutorial-progress';
  progressShell.style.cssText = [
    'position: absolute',
    'top: 34px',
    'right: 22px',
    'width: 280px',
    'display: flex',
    'flex-direction: column',
    'align-items: stretch',
    'gap: 8px',
    'transform-origin: center',
    'transition: transform 160ms cubic-bezier(0.2,0,0,1)',
    'pointer-events: none',
  ].join('; ');

  const progressTrack = document.createElement('div');
  progressTrack.dataset.testid = 'live-tutorial-progress-track';
  progressTrack.style.cssText = [
    'height: 36px',
    'border-radius: 10px',
    'background: rgba(34,36,44,0.92)',
    'border: 4px solid #111111',
    'box-shadow: 0 3px 0 rgba(0,0,0,0.46)',
    'overflow: hidden',
    'box-sizing: border-box',
  ].join('; ');

  const progressFill = document.createElement('div');
  progressFill.dataset.testid = 'live-tutorial-progress-fill';
  progressFill.style.cssText = [
    'height: 100%',
    'width: 0%',
    'border-radius: 6px',
    'background: linear-gradient(to bottom, #FFC84C, #EA8C22)',
    'box-shadow: inset 0 -2px 0 rgba(0,0,0,0.18)',
    'transition: width 180ms cubic-bezier(0.2,0,0,1)',
  ].join('; ');
  progressTrack.appendChild(progressFill);

  const progressLabel = document.createElement('div');
  progressLabel.dataset.testid = 'live-tutorial-progress-label';
  progressLabel.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 36px',
    'font-weight: 900',
    'line-height: 1',
    'letter-spacing: 0',
    'text-align: center',
    'text-shadow: 0 3px 0 #000000, 0 6px 14px rgba(0,0,0,0.62)',
    'white-space: nowrap',
  ].join('; ');
  progressShell.append(progressTrack, progressLabel);
  root.appendChild(progressShell);
  progressShell.style.display = options.showProgress === false ? 'none' : 'flex';

  const icon = document.createElement('div');
  icon.dataset.testid = 'live-tutorial-icon';
  icon.style.cssText = [
    'width: 44px',
    'height: 44px',
    'display: grid',
    'place-items: center',
    'border-radius: 10px',
    'background: linear-gradient(135deg, #9F5CFF, #542B9A)',
    'border: 4px solid #2B1854',
    'box-shadow: 0 4px 0 rgba(0,0,0,0.36)',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 13px',
    'font-weight: 900',
    'color: #FFFFFF',
    'text-shadow: 0 2px 0 #000000',
    'flex-shrink: 0',
  ].join('; ');

  const promptPanel = document.createElement('div');
  promptPanel.dataset.testid = 'live-tutorial-panel';
  promptPanel.style.cssText = [
    'position: absolute',
    'left: 50%',
    'top: 96px',
    'transform: translateX(-50%)',
    'width: 960px',
    'max-width: calc(100vw - 40px)',
    'border-radius: 12px',
    'background: linear-gradient(to bottom, #FFC84C, #EA8C22)',
    'border: 5px solid #8A5018',
    'outline: 4px solid #111111',
    'box-shadow: 0 7px 0 rgba(0,0,0,0.35)',
    'display: grid',
    'grid-template-columns: auto minmax(0, 1fr) auto',
    'align-items: center',
    'gap: 18px',
    'padding: 22px 26px',
    'box-sizing: border-box',
    'pointer-events: none',
  ].join('; ');
  const promptTextWrap = document.createElement('div');
  promptTextWrap.style.cssText = [
    'min-width: 0',
    'display: flex',
    'flex-direction: column',
    'gap: 3px',
  ].join('; ');
  const title = document.createElement('div');
  title.dataset.testid = 'live-tutorial-title';
  title.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 22px',
    'font-weight: 900',
    'line-height: 1',
    'letter-spacing: 0',
    'color: rgba(255,255,255,0.92)',
    'text-transform: uppercase',
    'text-shadow: 0 2px 0 rgba(0,0,0,0.65)',
  ].join('; ');
  const prompt = document.createElement('div');
  prompt.dataset.testid = 'live-tutorial-prompt';
  prompt.style.cssText = [
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 40px',
    'font-weight: 900',
    'line-height: 1.08',
    'letter-spacing: 0',
    'color: #FFFFFF',
    'text-shadow: 0 3px 0 #000000, 0 5px 10px rgba(0,0,0,0.35)',
  ].join('; ');
  const detail = document.createElement('div');
  detail.dataset.testid = 'live-tutorial-detail';
  detail.style.cssText = [
    `font-family: ${FONT_BODY}`,
    'font-size: 20px',
    'font-weight: 900',
    'line-height: 1.18',
    'margin-top: 9px',
    'color: #FFFFFF',
    'text-shadow: 0 2px 0 #000000, 0 4px 10px rgba(0,0,0,0.58)',
  ].join('; ');
  promptTextWrap.append(title, prompt, detail);
  const controls = document.createElement('div');
  controls.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: flex-end',
    'gap: 8px',
    'flex-shrink: 0',
    'pointer-events: auto',
  ].join('; ');
  const hide = document.createElement('button');
  hide.type = 'button';
  hide.dataset.testid = 'live-tutorial-hide';
  hide.textContent = 'HIDE';
  hide.style.cssText = [
    'height: 44px',
    'padding: 0 10px',
    'border-radius: 8px',
    'border: 2px solid rgba(0,0,0,0.34)',
    'background: rgba(255,255,255,0.22)',
    'color: #FFFFFF',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 12px',
    'font-weight: 900',
    'text-shadow: 0 2px 0 #000000',
    'cursor: pointer',
    options.showHideButton === false ? 'display: none' : '',
  ].join('; ');
  const next = document.createElement('button');
  next.type = 'button';
  next.dataset.testid = 'live-tutorial-next';
  next.style.cssText = [
    'height: 44px',
    'padding: 0 14px',
    'border-radius: 8px',
    'border: 2px solid #2B5B18',
    'background: linear-gradient(to bottom, #74C957, #4F9D36)',
    'color: #FFFFFF',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 13px',
    'font-weight: 900',
    'text-shadow: 0 2px 0 #000000',
    'box-shadow: 0 3px 0 rgba(0,0,0,0.30)',
    'cursor: pointer',
  ].join('; ');
  if (options.showHideButton !== false) {
    controls.append(hide);
  }
  controls.append(next);
  promptPanel.append(icon, promptTextWrap, controls);
  root.appendChild(promptPanel);

  const minimizedPill = document.createElement('button');
  minimizedPill.type = 'button';
  minimizedPill.dataset.testid = 'live-tutorial-minimized-pill';
  minimizedPill.style.cssText = [
    'position: absolute',
    'right: 22px',
    'bottom: 24px',
    'min-height: 42px',
    'max-width: min(320px, calc(100vw - 44px))',
    'display: none',
    'align-items: center',
    'justify-content: center',
    'gap: 8px',
    'padding: 8px 14px',
    'border-radius: 10px',
    'border: 3px solid #111111',
    'background: linear-gradient(to bottom, #FFC84C, #EA8C22)',
    'box-shadow: 0 4px 0 rgba(0,0,0,0.35), 0 8px 18px rgba(0,0,0,0.25)',
    'color: #FFFFFF',
    `font-family: ${FONT_HEADLINE}`,
    'font-size: 16px',
    'font-weight: 900',
    'letter-spacing: 0',
    'text-shadow: 0 2px 0 #000000',
    'cursor: pointer',
    'pointer-events: auto',
    'white-space: nowrap',
    'overflow: hidden',
    'text-overflow: ellipsis',
  ].join('; ');
  root.appendChild(minimizedPill);

  function rightAvoidance(): number {
    return Math.max(0, Math.floor(options.getRightAvoidance?.() ?? 0));
  }

  function progressDisplayValue(): string {
    return options.showProgress === false ? 'none' : 'flex';
  }

  function minimizedPillText(): string {
    const step = options.steps[index]!;
    return options.minimizedText ?? `Tutorial: ${step.title}`;
  }

  function applyMinimizedState(): void {
    minimizedPill.textContent = minimizedPillText();
    minimizedPill.style.display = minimized ? 'flex' : 'none';
    promptPanel.style.display = minimized ? 'none' : 'grid';
    targetGlow.style.display = 'none';
    progressShell.style.display = minimized ? 'none' : progressDisplayValue();
  }

  function setMinimized(nextMinimized: boolean): void {
    if (disposed || minimized === nextMinimized) return;
    minimized = nextMinimized;
    applyMinimizedState();
    if (minimized) {
      options.onMinimize?.();
      return;
    }
    options.onExpand?.();
    requestAnimationFrame(layout);
  }

  function applyResponsiveSizing(): void {
    const narrow = window.innerWidth < 700;
    const centeredMobile = narrow && options.mobileLayout === 'center';
    const step = options.steps[index]!;
    const usesDiceIcon = step.iconKind !== 'text';
    progressShell.style.width = narrow ? '220px' : '280px';
    progressShell.style.top = narrow ? '26px' : '34px';
    progressLabel.style.fontSize = narrow ? '26px' : '36px';
    progressTrack.style.height = narrow ? '28px' : '36px';
    promptPanel.style.width = narrow ? 'calc(100vw - 24px)' : '960px';
    promptPanel.style.maxWidth = narrow ? 'calc(100vw - 24px)' : 'calc(100vw - 40px)';
    promptPanel.style.gridTemplateColumns = narrow
      ? 'auto minmax(0, 1fr)'
      : 'auto minmax(0, 1fr) auto';
    promptPanel.style.padding = narrow ? '14px 16px' : '22px 26px';
    promptPanel.style.gap = narrow ? '12px' : '18px';
    title.style.fontSize = narrow ? '16px' : '22px';
    prompt.style.fontSize = narrow ? (centeredMobile ? '24px' : '27px') : '40px';
    detail.style.fontSize = narrow ? (centeredMobile ? '15px' : '16px') : '20px';
    icon.style.width = usesDiceIcon ? (narrow ? '58px' : '76px') : narrow ? '50px' : '64px';
    icon.style.height = usesDiceIcon ? (narrow ? '58px' : '76px') : narrow ? '50px' : '64px';
    icon.style.fontSize = narrow ? '13px' : '15px';
    next.style.height = narrow ? '40px' : '48px';
    next.style.fontSize = narrow ? '13px' : '16px';
    hide.style.height = narrow ? '44px' : '44px';
    next.style.height = narrow ? '44px' : '48px';
    controls.style.gridColumn = narrow ? '1 / -1' : '';
    controls.style.justifyContent = narrow ? 'flex-end' : 'flex-end';
    promptPanel.style.maxHeight = centeredMobile
      ? 'calc(var(--app-viewport-height, 100vh) - 236px)'
      : '';
    promptPanel.style.overflowY = centeredMobile ? 'auto' : '';
    promptPanel.style.overscrollBehavior = centeredMobile ? 'contain' : '';
  }

  function layout(): void {
    if (disposed) return;
    applyResponsiveSizing();
    const step = options.steps[index]!;
    const target = step.target?.() ?? null;
    const avoidRight = rightAvoidance();
    const avoidProgressRight = Math.max(avoidRight, options.getProgressRightAvoidance?.() ?? 0);
    const narrow = window.innerWidth < 700;
    const viewportW = Math.round(window.visualViewport?.width ?? window.innerWidth);
    const viewportH = Math.round(window.visualViewport?.height ?? window.innerHeight);
    const progressRight = Math.max(22, 22 + avoidProgressRight);
    progressShell.style.right = `${progressRight}px`;
    minimizedPill.style.right = `${Math.max(22, 22 + avoidProgressRight)}px`;

    if (minimized) {
      applyMinimizedState();
      return;
    }

    const rect = target?.getBoundingClientRect() ?? null;
    if (narrow && options.mobileLayout === 'center') {
      targetGlow.style.display =
        target == null || rect == null || rect.width <= 0 || rect.height <= 0 ? 'none' : 'block';
      if (targetGlow.style.display === 'block' && rect != null) {
        targetGlow.style.left = `${Math.max(8, rect.left - 8)}px`;
        targetGlow.style.top = `${Math.max(8, rect.top - 8)}px`;
        targetGlow.style.width = `${rect.width + 16}px`;
        targetGlow.style.height = `${rect.height + 16}px`;
      }
      const panelRect = promptPanel.getBoundingClientRect();
      const topReserve = 98;
      const bottomReserve = 138;
      const availableHeight = Math.max(220, viewportH - topReserve - bottomReserve);
      promptPanel.style.maxHeight = `${availableHeight}px`;
      promptPanel.style.left = '12px';
      promptPanel.style.right = '12px';
      promptPanel.style.top = `${Math.max(
        topReserve,
        Math.round((viewportH - panelRect.height) / 2),
      )}px`;
      promptPanel.style.bottom = '';
      promptPanel.style.transform = 'none';
      return;
    }

    if (target == null || rect == null || rect.width <= 0 || rect.height <= 0) {
      targetGlow.style.display = 'none';
      if (narrow) {
        promptPanel.style.left = '12px';
        promptPanel.style.right = '12px';
        promptPanel.style.top = '';
        promptPanel.style.bottom = 'max(12px, env(safe-area-inset-bottom))';
        promptPanel.style.transform = 'none';
        return;
      }
      const availableWidth = Math.max(0, viewportW - avoidRight);
      promptPanel.style.left = avoidRight > 0 ? `${availableWidth / 2}px` : '50%';
      promptPanel.style.right = '';
      const top = 96 + (step.placementOffsetY ?? 0);
      promptPanel.style.top = step.placement === 'bottom' ? '' : `${Math.max(14, top)}px`;
      promptPanel.style.bottom = step.placement === 'bottom' ? 'clamp(18px, 4vh, 38px)' : '';
      promptPanel.style.transform = 'translateX(-50%)';
      return;
    }

    targetGlow.style.display = 'block';
    targetGlow.style.left = `${Math.max(8, rect.left - 8)}px`;
    targetGlow.style.top = `${Math.max(8, rect.top - 8)}px`;
    targetGlow.style.width = `${rect.width + 16}px`;
    targetGlow.style.height = `${rect.height + 16}px`;

    if (narrow) {
      const targetCenterY = rect.top + rect.height / 2;
      promptPanel.style.left = '12px';
      promptPanel.style.right = '12px';
      promptPanel.style.transform = 'none';
      if (targetCenterY > viewportH * 0.55) {
        promptPanel.style.top = 'max(96px, calc(env(safe-area-inset-top) + 72px))';
        promptPanel.style.bottom = '';
      } else {
        promptPanel.style.top = '';
        promptPanel.style.bottom = 'max(12px, env(safe-area-inset-bottom))';
      }
      return;
    }

    const panelRect = promptPanel.getBoundingClientRect();
    const gap = 18;
    const placement = step.placement ?? 'bottom';
    let left = rect.left + rect.width / 2 - panelRect.width / 2;
    let top = rect.bottom + gap;

    if (placement === 'top') {
      top = rect.top - panelRect.height - gap;
    } else if (placement === 'left') {
      left = rect.left - panelRect.width - gap;
      top = rect.top + rect.height / 2 - panelRect.height / 2;
    } else if (placement === 'right') {
      left = rect.right + gap;
      top = rect.top + rect.height / 2 - panelRect.height / 2;
    }

    left = Math.min(
      Math.max(14, left),
      Math.max(14, viewportW - avoidRight - panelRect.width - 14),
    );
    top += step.placementOffsetY ?? 0;
    top = Math.min(Math.max(14, top), Math.max(14, viewportH - panelRect.height - 14));
    promptPanel.style.left = `${left}px`;
    promptPanel.style.top = `${top}px`;
    promptPanel.style.right = '';
    promptPanel.style.bottom = '';
    promptPanel.style.transform = 'none';
  }

  function render(): void {
    const step = options.steps[index]!;
    title.textContent = step.title;
    icon.replaceChildren();
    if (step.iconKind !== 'text') {
      icon.appendChild(createD20Icon());
      icon.style.background = 'transparent';
      icon.style.border = 'none';
      icon.style.boxShadow = 'none';
      icon.style.borderRadius = '0';
    } else {
      icon.textContent = step.iconText ?? 'D20';
      icon.style.background = 'linear-gradient(135deg, #9F5CFF, #542B9A)';
      icon.style.border = '4px solid #2B1854';
      icon.style.boxShadow = '0 4px 0 rgba(0,0,0,0.36)';
      icon.style.borderRadius = '10px';
    }
    prompt.textContent = step.prompt;
    detail.textContent = step.detail ?? '';
    detail.style.display = step.detail == null ? 'none' : 'block';
    progressLabel.textContent = `Tutorial ${index + 1}/${options.steps.length}`;
    progressFill.style.width = `${Math.round(((index + 1) / options.steps.length) * 10000) / 100}%`;
    next.textContent =
      step.manualAdvance === true
        ? (step.waitingText ?? 'WAIT')
        : (step.advanceText ?? (index === options.steps.length - 1 ? 'DONE' : 'NEXT'));
    next.disabled = step.manualAdvance === true;
    next.style.opacity = step.manualAdvance === true ? '0.62' : '1';
    next.style.cursor = step.manualAdvance === true ? 'default' : 'pointer';
    hide.textContent = options.minimizeOnHide === true ? 'MINIMIZE' : 'HIDE';
    applyMinimizedState();
    options.onStepChange?.(index, step);
    requestAnimationFrame(layout);
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    document.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('resize', layout);
    window.visualViewport?.removeEventListener('resize', layout);
    window.visualViewport?.removeEventListener('scroll', layout);
    next.removeEventListener('click', handleNextClick);
    hide.removeEventListener('click', handleHideClick);
    minimizedPill.removeEventListener('click', handleMinimizedClick);
    root.remove();
  }

  function complete(): void {
    if (disposed) return;
    dispose();
    options.onComplete();
  }

  function advance(): void {
    if (disposed) return;
    const step = options.steps[index]!;
    if (options.canAdvance?.(index, step) === false) {
      pulse();
      return;
    }
    if (index < options.steps.length - 1) {
      index += 1;
      render();
      pulse();
      return;
    }
    complete();
  }

  function pulse(): void {
    if (disposed) return;
    const pulseTarget = minimized ? minimizedPill : progressShell;
    pulseTarget.style.transform = 'scale(1.12)';
    window.setTimeout(() => {
      if (!disposed) pulseTarget.style.transform = 'scale(1)';
    }, 170);
  }

  function handleNextClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    advance();
  }

  function handleHideClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (options.minimizeOnHide === true) {
      setMinimized(true);
      return;
    }
    options.onHideTutorials?.();
    complete();
  }

  function handleMinimizedClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    setMinimized(false);
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (options.enableKeyboardAdvance === false) return;
    if (options.steps[index]?.manualAdvance === true) return;
    if (isAdvanceKey(event)) advance();
  }

  render();
  next.addEventListener('click', handleNextClick);
  hide.addEventListener('click', handleHideClick);
  minimizedPill.addEventListener('click', handleMinimizedClick);
  document.addEventListener('keydown', handleKeydown);
  window.addEventListener('resize', layout);
  window.visualViewport?.addEventListener('resize', layout);
  window.visualViewport?.addEventListener('scroll', layout);
  options.mount.appendChild(root);

  return {
    element: root,
    get active() {
      return !disposed;
    },
    get minimized() {
      return minimized;
    },
    advance,
    layout,
    pulse,
    minimize: () => setMinimized(true),
    expand: () => setMinimized(false),
    dispose,
  };
}
